import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { maintenanceFeeApi, MaintenanceFeeChargeAttempt } from '../api/maintenanceFee';
import { adminApi, Client, ClientAccountListItem } from '../api/admin';
import { providersApi, Provider } from '../api/providers';
import { format } from '../utils/dateFormat';
import { getErrorMessage } from '../api/client';
import { notifyError, notifyErrorMessage, notifySuccess } from '../utils/notify';
import { X, Plus, Calendar, DollarSign, User, CheckCircle, SkipForward, AlertCircle } from 'lucide-react';
import { getClientDisplayName, getClientOptionLabel } from '../utils/clientDisplay';
import EnglishDatePicker from '../components/common/EnglishDatePicker';

const emptyScheduleData = {
  clientId: '',
  clientAccountId: '',
  feeType: 'MAINTENANCE_FEE' as 'MAINTENANCE_FEE' | 'ONBOARDING_FEE',
  billingPeriodFrom: '',
  billingPeriodTo: '',
  scheduledChargeDate: '',
  chargeAmount: '',
  chargeCurrencyId: '',
  feeRuleId: '',
  notes: '',
};

function formatAccountOption(
  account: ClientAccountListItem,
  providerNameById: Map<string, string>,
): string {
  const number = account.accountNumber?.trim() || account.iban?.trim() || account.id.slice(0, 8);
  const balance = Number(account.availableBalance ?? 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  const currency = account.currencyId || '';
  const provider =
    account.providerName?.trim() ||
    providerNameById.get(account.providerId) ||
    account.bankName?.trim() ||
    'Unknown provider';
  return `${number} · ${balance} ${currency} · ${provider}`;
}

function MaintenanceFeePage() {
  const [fees, setFees] = useState<MaintenanceFeeChargeAttempt[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'all' | 'pending' | 'charged' | 'skipped'>('all');
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [clients, setClients] = useState<Client[]>([]);
  const [providers, setProviders] = useState<Provider[]>([]);
  const [clientAccounts, setClientAccounts] = useState<ClientAccountListItem[]>([]);
  const [accountsLoading, setAccountsLoading] = useState(false);

  const [scheduleModal, setScheduleModal] = useState<{ open: boolean }>({ open: false });
  const [scheduleData, setScheduleData] = useState(emptyScheduleData);

  const [skipModal, setSkipModal] = useState<{ id: string; open: boolean }>({ id: '', open: false });
  const [skipReason, setSkipReason] = useState('');

  const providerNameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const p of providers) {
      if (p.id) map.set(p.id, p.name || p.id);
    }
    return map;
  }, [providers]);

  const activeAccounts = useMemo(
    () =>
      clientAccounts.filter((a) => String(a.status || '').toUpperCase() === 'ACTIVE'),
    [clientAccounts],
  );

  const fetchFees = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      let response;
      if (activeTab === 'pending') {
        response = await maintenanceFeeApi.getPending();
      } else if (activeTab === 'all') {
        response = await maintenanceFeeApi.getAll();
      } else if (activeTab === 'charged') {
        response = await maintenanceFeeApi.getCharged();
      } else if (activeTab === 'skipped') {
        response = await maintenanceFeeApi.getSkipped();
      }

      setFees(response?.data || []);
    } catch (err: unknown) {
      setError(getErrorMessage(err, 'Failed to fetch maintenance fees'));
    } finally {
      setLoading(false);
    }
  }, [activeTab]);

  useEffect(() => {
    fetchFees();
  }, [activeTab, fetchFees]);

  useEffect(() => {
    void adminApi.getClients()
      .then((res) => setClients(res.data || []))
      .catch(() => setClients([]));
    void providersApi.getAll()
      .then((data) => setProviders(data || []))
      .catch(() => setProviders([]));
  }, []);

  useEffect(() => {
    const clientId = scheduleData.clientId;
    if (!clientId || !scheduleModal.open) {
      setClientAccounts([]);
      return;
    }

    let cancelled = false;
    setAccountsLoading(true);
    void adminApi
      .getClientAccountsByClientId(clientId)
      .then((res) => {
        if (cancelled) return;
        setClientAccounts(res.data || []);
      })
      .catch(() => {
        if (cancelled) return;
        setClientAccounts([]);
      })
      .finally(() => {
        if (!cancelled) setAccountsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [scheduleData.clientId, scheduleModal.open]);

  useEffect(() => {
    if (!scheduleData.clientAccountId) return;
    const selected = activeAccounts.find((a) => a.id === scheduleData.clientAccountId);
    if (!selected) {
      setScheduleData((prev) => ({ ...prev, clientAccountId: '', chargeCurrencyId: '' }));
      return;
    }
    const accountCurrency = String(selected.currencyId || '').trim().toUpperCase();
    if (accountCurrency && accountCurrency !== scheduleData.chargeCurrencyId) {
      setScheduleData((prev) => ({ ...prev, chargeCurrencyId: accountCurrency }));
    }
  }, [activeAccounts, scheduleData.clientAccountId, scheduleData.chargeCurrencyId]);

  const handleClientChange = (clientId: string) => {
    setScheduleData((prev) => ({
      ...prev,
      clientId,
      clientAccountId: '',
      chargeCurrencyId: '',
    }));
  };

  const handleAccountChange = (clientAccountId: string) => {
    const account = activeAccounts.find((a) => a.id === clientAccountId);
    const accountCurrency = String(account?.currencyId || '').trim().toUpperCase();
    setScheduleData((prev) => ({
      ...prev,
      clientAccountId,
      chargeCurrencyId: accountCurrency || prev.chargeCurrencyId,
    }));
  };

  const handleSchedule = async () => {
    const isOnboardingFee = scheduleData.feeType === 'ONBOARDING_FEE';
    if (
      !scheduleData.clientId ||
      !scheduleData.clientAccountId ||
      !scheduleData.scheduledChargeDate ||
      (!isOnboardingFee && (!scheduleData.billingPeriodFrom || !scheduleData.billingPeriodTo))
    ) {
      notifyErrorMessage('Please fill in all required fields.');
      return;
    }
    const chargeAmount = Number(scheduleData.chargeAmount);
    if (!Number.isFinite(chargeAmount) || chargeAmount <= 0) {
      notifyErrorMessage('Charge amount must be greater than zero.');
      return;
    }
    if (!scheduleData.chargeCurrencyId) {
      notifyErrorMessage('Charge currency is required.');
      return;
    }
    if (scheduleData.feeRuleId.trim()) {
      const uuidRe =
        /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
      if (!uuidRe.test(scheduleData.feeRuleId.trim())) {
        notifyErrorMessage('Fee Rule ID must be a valid UUID.');
        return;
      }
    }

    const billingFrom = isOnboardingFee
      ? scheduleData.scheduledChargeDate
      : scheduleData.billingPeriodFrom;
    const billingTo = isOnboardingFee
      ? scheduleData.scheduledChargeDate
      : scheduleData.billingPeriodTo;

    try {
      setActionLoading('schedule');
      await maintenanceFeeApi.schedule({
        clientId: scheduleData.clientId,
        clientAccountId: scheduleData.clientAccountId,
        feeType: scheduleData.feeType,
        billingPeriodFrom: billingFrom,
        billingPeriodTo: billingTo,
        scheduledChargeDate: scheduleData.scheduledChargeDate,
        chargeAmount,
        chargeCurrencyId: scheduleData.chargeCurrencyId,
        feeRuleId: scheduleData.feeRuleId.trim() || undefined,
        notes: scheduleData.notes || undefined,
      });
      setScheduleModal({ open: false });
      setScheduleData(emptyScheduleData);
      setClientAccounts([]);
      await fetchFees();
      notifySuccess('Maintenance fee scheduled.');
    } catch (e: unknown) {
      notifyError(e, 'Failed to schedule maintenance fee');
    } finally {
      setActionLoading(null);
    }
  };

  const handleCharge = async (id: string) => {
    if (!window.confirm('Charge this maintenance fee? This will create a ledger transaction.')) return;

    try {
      setActionLoading(id);
      await maintenanceFeeApi.charge(id);
      await fetchFees();
      notifySuccess('Fee charged.');
    } catch (e: unknown) {
      notifyError(e, 'Failed to charge fee');
    } finally {
      setActionLoading(null);
    }
  };

  const handleSkip = async () => {
    if (!skipReason.trim()) {
      notifyErrorMessage('Skip reason is required.');
      return;
    }

    try {
      setActionLoading(skipModal.id);
      await maintenanceFeeApi.skip(skipModal.id, { reason: skipReason });
      setSkipModal({ id: '', open: false });
      setSkipReason('');
      await fetchFees();
      notifySuccess('Fee skipped.');
    } catch (e: unknown) {
      notifyError(e, 'Failed to skip fee');
    } finally {
      setActionLoading(null);
    }
  };

  const handleProcessDue = async () => {
    if (!window.confirm('Process all due maintenance fees?')) return;

    try {
      setActionLoading('process');
      const response = await maintenanceFeeApi.processDue();
      notifySuccess(`Processed ${response.data.processed} due fee(s).`);
      await fetchFees();
    } catch (e: unknown) {
      notifyError(e, 'Failed to process due fees');
    } finally {
      setActionLoading(null);
    }
  };

  const getStatusBadge = (status: string) => {
    const styles = {
      PENDING: 'bg-amber-100 text-amber-800',
      CHARGED: 'bg-green-100 text-green-800',
      SKIPPED: 'bg-gray-100 text-gray-800',
      FAILED: 'bg-red-100 text-red-800',
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${styles[status as keyof typeof styles] || 'bg-gray-100'}`}>
        {status}
      </span>
    );
  };

  const filteredFees = fees;

  return (
    <div className="p-6">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Maintenance Fees</h1>
          <p className="text-gray-600">Manage periodic maintenance fee charges for clients</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={handleProcessDue}
            disabled={actionLoading === 'process'}
            className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 text-sm flex items-center gap-2"
          >
            <Calendar className="h-4 w-4" />
            {actionLoading === 'process' ? 'Processing...' : 'Process Due'}
          </button>
          <button
            onClick={() => setScheduleModal({ open: true })}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm flex items-center gap-2"
          >
            <Plus className="h-4 w-4" />
            Schedule Fee
          </button>
        </div>
      </div>

      <div className="flex gap-2 mb-4">
        {(['all', 'pending', 'charged', 'skipped'] as const).map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`px-4 py-2 rounded-lg text-sm font-medium ${
              activeTab === tab
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            {tab.charAt(0).toUpperCase() + tab.slice(1)}
            {tab === activeTab && (
              <span className="ml-2 bg-white bg-opacity-20 px-2 py-0.5 rounded-full text-xs">
                {filteredFees.length}
              </span>
            )}
          </button>
        ))}
      </div>

      {loading && <div className="text-center py-8 text-gray-500">Loading...</div>}
      {error && <div className="bg-red-50 text-red-700 p-4 rounded-lg mb-4">{error}</div>}

      {!loading && !error && (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="min-w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Client</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Fee Type</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Period</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Scheduled Date</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Fee Amount</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Notes</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filteredFees.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-4 py-8 text-center text-gray-500">
                    No maintenance fees found
                  </td>
                </tr>
              ) : (
                filteredFees.map((fee) => (
                  <tr key={fee.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <User className="h-4 w-4 text-gray-400" />
                        <div>
                          <div className="text-sm font-medium text-gray-900">{fee.clientName || getClientDisplayName(clients.find((c) => c.id === fee.clientId)) || fee.clientId}</div>
                          <div className="text-xs text-gray-500">{fee.primaryAccountCurrency}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold ${
                        fee.feeType === 'ONBOARDING_FEE' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'
                      }`}>
                        {fee.feeType === 'ONBOARDING_FEE' ? 'Onboarding fee' : 'Monthly maintenance'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-900">
                      {format(fee.billingPeriodFrom)} - {format(fee.billingPeriodTo)}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-900">
                      <div className="flex items-center gap-1">
                        <Calendar className="h-4 w-4 text-gray-400" />
                        {format(fee.scheduledChargeDate)}
                      </div>
                    </td>
                    <td className="px-4 py-3">{getStatusBadge(fee.chargeStatus)}</td>
                    <td className="px-4 py-3 text-sm text-gray-900">
                      {(fee.feeAmount ?? fee.chargeAmount) ? (
                        <div className="flex items-center gap-1">
                          <DollarSign className="h-4 w-4 text-gray-400" />
                          {(fee.feeAmount ?? fee.chargeAmount)} {fee.feeCurrency || fee.chargeCurrencyId}
                        </div>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500 max-w-xs truncate">
                      {fee.notes || fee.skipReason || '-'}
                    </td>
                    <td className="px-4 py-3">
                      {fee.chargeStatus === 'PENDING' && (
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleCharge(fee.id)}
                            disabled={actionLoading === fee.id}
                            className="px-2 py-1 bg-green-600 text-white text-xs rounded hover:bg-green-700 disabled:opacity-50 flex items-center gap-1"
                          >
                            <CheckCircle className="h-3 w-3" />
                            {actionLoading === fee.id ? '...' : 'Charge'}
                          </button>
                          <button
                            onClick={() => setSkipModal({ id: fee.id, open: true })}
                            disabled={actionLoading === fee.id}
                            className="px-2 py-1 bg-gray-500 text-white text-xs rounded hover:bg-gray-600 disabled:opacity-50 flex items-center gap-1"
                          >
                            <SkipForward className="h-3 w-3" />
                            Skip
                          </button>
                        </div>
                      )}
                      {fee.chargeStatus === 'FAILED' && (
                        <div className="flex items-center gap-1 text-red-600 text-xs">
                          <AlertCircle className="h-3 w-3" />
                          {fee.skipReason || 'Failed'}
                        </div>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {scheduleModal.open && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Schedule Maintenance Fee</h3>
              <button
                onClick={() => {
                  setScheduleModal({ open: false });
                  setScheduleData(emptyScheduleData);
                  setClientAccounts([]);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Client *</label>
                <select
                  value={scheduleData.clientId}
                  onChange={(e) => handleClientChange(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none"
                >
                  <option value="">Select client</option>
                  {clients.map((client) => (
                    <option key={client.id} value={client.id}>
                      {getClientOptionLabel(client)}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Debit Account *</label>
                <select
                  value={scheduleData.clientAccountId}
                  onChange={(e) => handleAccountChange(e.target.value)}
                  disabled={!scheduleData.clientId || accountsLoading}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none disabled:bg-gray-50"
                >
                  <option value="">
                    {!scheduleData.clientId
                      ? 'Select client first'
                      : accountsLoading
                        ? 'Loading accounts...'
                        : activeAccounts.length === 0
                          ? 'No active accounts'
                          : 'Select account'}
                  </option>
                  {activeAccounts.map((account) => (
                    <option key={account.id} value={account.id}>
                      {formatAccountOption(account, providerNameById)}
                    </option>
                  ))}
                </select>
                <p className="mt-1 text-xs text-gray-500">
                  Shows account number, available balance, and bank/provider. Charge currency follows the selected account.
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Fee Type *</label>
                <select
                  value={scheduleData.feeType}
                  onChange={(e) =>
                    setScheduleData((prev) => ({
                      ...prev,
                      feeType: e.target.value as 'MAINTENANCE_FEE' | 'ONBOARDING_FEE',
                    }))
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none"
                >
                  <option value="MAINTENANCE_FEE">Monthly maintenance</option>
                  <option value="ONBOARDING_FEE">Onboarding fee (one-time)</option>
                </select>
              </div>
              <div className="flex gap-3">
                <div className="flex-1">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Period From *</label>
                  <EnglishDatePicker
                    value={scheduleData.billingPeriodFrom}
                    onChange={(value) => setScheduleData({ ...scheduleData, billingPeriodFrom: value })}
                    disabled={scheduleData.feeType === 'ONBOARDING_FEE'}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none"
                  />
                </div>
                <div className="flex-1">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Period To *</label>
                  <EnglishDatePicker
                    value={scheduleData.billingPeriodTo}
                    onChange={(value) => setScheduleData({ ...scheduleData, billingPeriodTo: value })}
                    disabled={scheduleData.feeType === 'ONBOARDING_FEE'}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Charge Date *</label>
                <EnglishDatePicker
                  value={scheduleData.scheduledChargeDate}
                  onChange={(value) => setScheduleData({ ...scheduleData, scheduledChargeDate: value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none"
                />
              </div>
              <div className="flex gap-3">
                <div className="flex-1">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Charge Amount *</label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={scheduleData.chargeAmount}
                    onChange={(e) => setScheduleData({ ...scheduleData, chargeAmount: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none"
                    placeholder="0.00"
                  />
                </div>
                <div className="flex-1">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Charge Currency *</label>
                  <input
                    type="text"
                    readOnly
                    value={scheduleData.chargeCurrencyId}
                    placeholder={scheduleData.clientAccountId ? '' : 'Select debit account'}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-gray-50 text-gray-700"
                  />
                  <p className="mt-1 text-xs text-gray-500">Taken from the selected debit account</p>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Fee Rule ID (optional)</label>
                <input
                  type="text"
                  value={scheduleData.feeRuleId}
                  onChange={(e) => setScheduleData({ ...scheduleData, feeRuleId: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none"
                  placeholder="Fee Rule UUID"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                <textarea
                  value={scheduleData.notes}
                  onChange={(e) => setScheduleData({ ...scheduleData, notes: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none"
                  rows={2}
                  placeholder="Optional notes"
                />
              </div>
            </div>
            <div className="flex gap-3 mt-4">
              <button
                onClick={() => {
                  setScheduleModal({ open: false });
                  setScheduleData(emptyScheduleData);
                  setClientAccounts([]);
                }}
                className="flex-1 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 text-sm"
              >
                Cancel
              </button>
              <button
                onClick={handleSchedule}
                disabled={actionLoading === 'schedule'}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm disabled:opacity-50"
              >
                {actionLoading === 'schedule' ? 'Scheduling...' : 'Schedule'}
              </button>
            </div>
          </div>
        </div>
      )}

      {skipModal.open && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Skip Maintenance Fee</h3>
              <button onClick={() => setSkipModal({ id: '', open: false })} className="text-gray-400 hover:text-gray-600">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Reason for skipping *</label>
                <textarea
                  value={skipReason}
                  onChange={(e) => setSkipReason(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none"
                  rows={3}
                  placeholder="Why is this fee being skipped? (required for audit)"
                />
              </div>
            </div>
            <div className="flex gap-3 mt-4">
              <button
                onClick={() => setSkipModal({ id: '', open: false })}
                className="flex-1 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 text-sm"
              >
                Cancel
              </button>
              <button
                onClick={handleSkip}
                disabled={actionLoading === skipModal.id || !skipReason.trim()}
                className="flex-1 px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 text-sm disabled:opacity-50"
              >
                {actionLoading === skipModal.id ? 'Skipping...' : 'Skip Fee'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default MaintenanceFeePage;
